import { createAsyncThunk } from "@reduxjs/toolkit"

import apiClient from "src/apiClient"
import { loadFromLocalStorage } from "src/localStorage"
import userReducers from "../reducers"

/* One refresh at a time (security audit, 2026-10-25). With refresh rotation on the server (gamekit
   0.11.0) every refresh spends the token it was given and answers with a new one, so two refreshes
   racing — the REST interceptor and the socket's reconnect, say — would send the same token twice,
   and the second would be refused as already used and log the player out. Every caller while one
   is in flight waits for that one's answer instead. And the token sent is the newest this browser
   holds: another tab of the same player may have just rotated it, writing the new one to storage
   while this tab's store still holds the spent one. */
let inFlight = null

const refreshOnce = async (refresh) => {
  const tokenResponse = await apiClient.post("/token/refresh/", { refresh })
  // A server that rotates answers with the next refresh token beside the access token: keep it, or
  // the next refresh would present the one just spent. A server that does not rotate sends no
  // `refresh`, and the stored one stays.
  const rotated = tokenResponse.data?.refresh
  return {
    accessToken: tokenResponse.data.access,
    ...(typeof rotated === "string" && rotated ? { refreshToken: rotated } : {}),
  }
}

export const thunk = createAsyncThunk("user/getNewAccessToken", async (_, { getState, rejectWithValue }) => {
  try {
    if (!inFlight) {
      const refresh = loadFromLocalStorage().refreshToken || getState().user.refreshToken
      inFlight = refreshOnce(refresh).finally(() => {
        inFlight = null
      })
    }
    return await inFlight
  } catch (error) {
    // Keep the status: the interceptor must tell "this refresh token is no good" (401 — log out)
    // from "the server is down right now" (5xx, no response — keep the session, try later).
    return rejectWithValue({ status: error.response?.status ?? null, data: error.response?.data ?? null })
  }
})

export const onSucceed = userReducers.setAccessToken

const thunkObject = { thunk, onSucceed }
export default thunkObject
